/**
 * Funnel Configuration System
 *
 * Provides configurable marketing funnel stages with keyword-based classification.
 * This system is industry-agnostic and supports multiple funnel templates.
 *
 * Foundation for future funnel setting feature - users will be able to customize
 * their funnel stages, keywords, and metrics through UI.
 *
 * The type layer and the nine template literals now live in ./funnelTypes and
 * ./funnelTemplates. Both are re-exported below, so every existing import from
 * '@/config/funnelConfig' keeps working; this file keeps the logic.
 */

export type { FunnelStageConfig, FunnelConfig } from './funnelTypes';
export * from './funnelTemplates';

// And imported for this module's own use — a re-export does not bring a name
// into the re-exporting module's scope.
import type { FunnelConfig, FunnelStageConfig } from './funnelTypes';
import {
  DEFAULT_FUNNEL, AUTOMOTIVE_FUNNEL, ECOMMERCE_FUNNEL, REAL_ESTATE_FUNNEL,
  INSURANCE_FUNNEL, LEAD_GEN_FUNNEL, SUPPLEMENT_FUNNEL, EV_FUNNEL, FMCG_FUNNEL,
} from './funnelTemplates';



// ============================================================================
// FUNNEL TEMPLATES REGISTRY
// ============================================================================

/**
 * All available funnel templates
 * Can be extended with more industry-specific funnels
 */
export const INDUSTRY_FUNNELS: Record<string, FunnelConfig> = {
  general: DEFAULT_FUNNEL,
  automotive: AUTOMOTIVE_FUNNEL,
  ecommerce: ECOMMERCE_FUNNEL,
  realestate: REAL_ESTATE_FUNNEL,
  insurance: INSURANCE_FUNNEL,
  lead_gen: LEAD_GEN_FUNNEL,
  supplement: SUPPLEMENT_FUNNEL,
  ev: EV_FUNNEL,
  fmcg: FMCG_FUNNEL,
}

// ============================================================================
// PLATFORM OBJECTIVE TO FUNNEL STAGE MAPPING
// ============================================================================

/**
 * Platform-specific campaign objective to funnel stage mapping.
 *
 * Maps each platform's campaign objective values to standardized funnel stages:
 * - 'aware': Awareness/Reach campaigns
 * - 'consider': Consideration/Traffic/Engagement campaigns
 * - 'convert': Conversion/Sales/Lead generation campaigns
 *
 * User decisions:
 * - Facebook LEAD_GENERATION → 'convert' (lead gen is a conversion action)
 * - Google performance_max → 'convert' (always conversion-focused)
 * - Demo distribution: 20% awareness / 50% consideration / 30% conversion
 */
export const PLATFORM_OBJECTIVE_TO_FUNNEL: Record<string, Record<string, string>> = {
  facebook: {
    // Awareness
    BRAND_AWARENESS: 'aware',
    REACH: 'aware',
    // Consideration
    TRAFFIC: 'consider',
    ENGAGEMENT: 'consider',
    VIDEO_VIEWS: 'consider',
    MESSAGES: 'consider',
    APP_INSTALLS: 'consider',
    // Conversion
    LEAD_GENERATION: 'convert', // User decision: map to conversion
    CONVERSIONS: 'convert',
    CATALOG_SALES: 'convert',
    STORE_TRAFFIC: 'convert',
  },
  google: {
    BRAND_AWARENESS: 'aware',
    TRAFFIC: 'consider',
    ENGAGE_CALLS: 'consider',
    STORE_VISITS: 'consider',
    SALES: 'convert',
    LEADS: 'convert',
    PERFORMANCE_MAX: 'convert', // User decision: always convert
  },
  tiktok: {
    REACH: 'aware',
    BRAND_AWARENESS: 'aware',
    TRAFFIC: 'consider',
    VIDEO_VIEWS: 'consider',
    APP_INSTALL: 'consider',
    CONVERSION: 'convert',
    CATALOG_SALES: 'convert',
  },
  line: {
    REACH: 'aware',
    TRAFFIC: 'consider',
    WEBSITE_CONVERSION: 'consider',
    CONVERSION: 'convert',
  },
}

/**
 * Platform key aliases for normalization
 * Helps match different platform identifier formats
 */
const PLATFORM_KEY_ALIASES: Record<string, string> = {
  // Facebook variants
  fb: 'facebook',
  meta: 'facebook',
  'facebook-ads': 'facebook',
  // Google variants - hyphenated (before underscore removal)
  gdn: 'google',
  'google-ads': 'google',
  'google-search': 'google',
  'google-display': 'google',
  'google-display-network': 'google',
  // Google variants - underscore/after underscore removal
  googledisplay: 'google',
  googlesearch: 'google',
  performancemax: 'google',
  // TikTok variants
  tt: 'tiktok',
  'tiktok-ads': 'tiktok',
  '@tiktok': 'tiktok',
}

/**
 * Normalize platform key to standard identifier
 *
 * @param platform - Platform identifier (e.g., 'facebook', 'fb', '@tiktok')
 * @returns Normalized platform key or undefined if not recognized
 */
function normalizePlatformKey(platform: string): string | undefined {
  const normalized = platform.toLowerCase().replace(/[@_\s-]/g, '')
  return PLATFORM_KEY_ALIASES[normalized] || normalized
}

/**
 * Get funnel stage from platform-specific campaign objective.
 *
 * This is the PRIMARY method for funnel classification when objective is available.
 * Keyword-based classification is only used as fallback when objective is missing.
 *
 * @param objective - Platform-specific objective value (e.g., 'BRAND_AWARENESS', 'CONVERSIONS')
 * @param platform - Platform identifier (e.g., 'facebook', 'google', 'tiktok', 'line')
 * @returns Funnel stage key ('aware', 'consider', 'convert') or undefined if not found
 *
 * @example
 * ```typescript
 * getFunnelStageFromObjective('BRAND_AWARENESS', 'facebook') // 'aware'
 * getFunnelStageFromObjective('performance_max', 'google') // 'convert'
 * getFunnelStageFromObjective('traffic', 'tiktok') // 'consider'
 * ```
 */
export function getFunnelStageFromObjective(
  objective: string,
  platform: string
): string | undefined {
  const normalizedPlatform = normalizePlatformKey(platform)
  if (!normalizedPlatform) return undefined

  const platformMapping = PLATFORM_OBJECTIVE_TO_FUNNEL[normalizedPlatform]
  if (!platformMapping) return undefined

  // Normalize objective key (uppercase, keep underscores as-is, replace spaces/hyphens with underscores)
  const normalizedObjective = objective.toUpperCase().replace(/[\s-]/g, '_')

  return platformMapping[normalizedObjective]
}

/**
 * Get funnel stage from generic objective value.
 *
 * Handles standardized objective values: 'awareness', 'consideration', 'conversion'
 *
 * @param objective - Generic objective value
 * @returns Funnel stage key or undefined
 */
export function getFunnelStageFromGenericObjective(objective: string): string | undefined {
  const normalized = objective.toLowerCase()
  if (normalized === 'awareness') return 'aware'
  if (normalized === 'consideration') return 'consider'
  if (normalized === 'conversion') return 'convert'
  return undefined
}

// ============================================================================
// CLASSIFICATION HELPER FUNCTIONS
// ============================================================================

/**
 * Classify a campaign into a funnel stage based on its name
 * Uses keyword matching from the funnel configuration
 *
 * @param campaignName - Name of the campaign to classify
 * @param funnel - Funnel configuration to use (defaults to DEFAULT_FUNNEL)
 * @returns The stage key (e.g., 'aware', 'consider', 'convert')
 */
export function classifyCampaignByFunnel(
  campaignName: string,
  funnel: FunnelConfig = DEFAULT_FUNNEL
): string {
  const text = campaignName.toLowerCase()

  // Check stages in reverse order (conversion first)
  // This ensures more specific conversion keywords match before general ones
  for (let i = funnel.stages.length - 1; i >= 0; i--) {
    const stage = funnel.stages[i]
    if (stage.keywords.some((keyword) => text.includes(keyword))) {
      return stage.key
    }
  }

  // Default to first stage (awareness) if no match
  return funnel.stages[0]?.key || 'aware'
}

/**
 * Get stage configuration by key
 *
 * @param stageKey - The stage key to look up
 * @param funnel - Funnel configuration to use
 * @returns The stage configuration or undefined if not found
 */
export function getStageConfig(
  stageKey: string,
  funnel: FunnelConfig = LEAD_GEN_FUNNEL
): FunnelStageConfig | undefined {
  return funnel.stages.find((stage) => stage.key === stageKey)
}

/**
 * Get all stage keys from a funnel
 *
 * @param funnel - Funnel configuration to use
 * @returns Array of stage keys in order
 */
export function getStageKeys(funnel: FunnelConfig = LEAD_GEN_FUNNEL): string[] {
  return funnel.stages.map((stage) => stage.key)
}

/**
 * Validate a funnel configuration
 * Ensures all required fields are present and valid
 *
 * @param funnel - Funnel configuration to validate
 * @returns True if valid, false otherwise
 */
export function validateFunnelConfig(funnel: FunnelConfig): boolean {
  if (!funnel.id || !funnel.name || !funnel.industry) {
    return false
  }

  if (!funnel.stages || funnel.stages.length === 0) {
    return false
  }

  for (const stage of funnel.stages) {
    if (!stage.key || !stage.label || !stage.description) {
      return false
    }
    if (!stage.keywords || !Array.isArray(stage.keywords)) {
      return false
    }
  }

  if (!funnel.metrics || !funnel.metrics.primary || !funnel.metrics.secondary) {
    return false
  }

  return true
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  DEFAULT_FUNNEL,
  AUTOMOTIVE_FUNNEL,
  ECOMMERCE_FUNNEL,
  REAL_ESTATE_FUNNEL,
  INSURANCE_FUNNEL,
  LEAD_GEN_FUNNEL,
  SUPPLEMENT_FUNNEL,
  EV_FUNNEL,
  FMCG_FUNNEL,
  INDUSTRY_FUNNELS,
  PLATFORM_OBJECTIVE_TO_FUNNEL,
  classifyCampaignByFunnel,
  getFunnelStageFromObjective,
  getFunnelStageFromGenericObjective,
  getStageConfig,
  getStageKeys,
  validateFunnelConfig,
}
